// src/components/order/OrdersTable.tsx
//
// OrdersTable (Standard)
// - Top: StandardTableToolbar (Total + Page Jump)
// - Middle: StandardTableFrame
// - Bottom: StandardPagination
// - Partner view hides the Stock Code column.
//
// Navigation policy (Orders-only exception):
// - Both Index cell and Order Code navigate to the order detail page for faster operations.

import React, { useCallback, useMemo } from 'react';
import { Box, Chip, TableCell, TableRow } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { toUsdDisplay } from '@utils/money';
import { UserGroup, type User } from '@models/user';
import type { OrderResponse } from '@apis/order';

import { getOrderStatusMeta } from '@constants/orderLabels';
import { utcToZonedTime, format as formatTz } from 'date-fns-tz';

import {
  StandardTableFrame,
  StandardTableToolbar,
  StandardPagination,
  cellSx,
} from '@components/common/table';

/**
 * Format reserved date as yyyy-MM-dd in KST.
 * Returns '-' when value is empty.
 */
const formatKSTDateOnly = (ts?: number | null) => {
  if (!ts) return '-';
  const d = new Date(ts);
  const kst = utcToZonedTime(d, 'Asia/Seoul');
  return formatTz(kst, 'yyyy-MM-dd');
};

/**
 * Format order date as yyyy-MM-dd HH:mm:ss in KST.
 * Returns '-' when value is empty.
 */
const formatKSTDateTime = (ts?: number | null) => {
  if (!ts) return '-';
  const d = new Date(ts);
  const kst = utcToZonedTime(d, 'Asia/Seoul');
  return formatTz(kst, 'yyyy-MM-dd HH:mm:ss');
};

interface OrdersTableProps {
  data: OrderResponse[];
  isLoading: boolean;
  totalCount?: number;
  page: number;
  pageSize: number;
  onChangePage: (event: React.ChangeEvent<unknown>, value: number) => void;
  user: User | null | undefined;
}

const OrdersTable = ({
  data,
  isLoading,
  totalCount,
  page,
  pageSize,
  onChangePage,
  user,
}: OrdersTableProps) => {
  const navigate = useNavigate();

  const rows = data ?? [];
  const isPartner = user?.group === UserGroup.PARTNER;

  // Partner view hides Stock Code column.
  const columnsCount = useMemo(() => (isPartner ? 10 : 11), [isPartner]);

  const handleChangePage = useCallback(
    (next: number) => onChangePage({} as any, next),
    [onChangePage],
  );

  const goDetail = useCallback(
    (orderId: number) => {
      if (!Number.isFinite(orderId) || orderId <= 0) return;
      navigate(`/orders/${orderId}`);
    },
    [navigate],
  );

  return (
    <Box>
      <StandardTableToolbar
        page={page}
        pageSize={pageSize}
        totalCount={totalCount ?? 0}
        onChangePage={handleChangePage}
        showTotalCount
        showPageJump
      />

      <Box mt={1}>
        <StandardTableFrame<OrderResponse>
          head={
            <TableRow>
              <TableCell width={70} align="right">
                #
              </TableCell>
              <TableCell align="left">Order Code</TableCell>
              <TableCell align="left">Product Code</TableCell>
              <TableCell align="left">Product Name</TableCell>

              {!isPartner && <TableCell align="center">Stock Code</TableCell>}

              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="center">Partner</TableCell>
              <TableCell align="center">Reserved</TableCell>
              <TableCell align="center">Order Date (KST)</TableCell>
              <TableCell align="center">Status</TableCell>
            </TableRow>
          }
          rows={rows}
          getRowKey={(order) => order.orderId}
          columnsCount={columnsCount}
          isLoading={isLoading}
          loadingVariant="skeleton"
          skeletonRows={6}
          emptyText="No orders found."
          minWidth={isPartner ? 1120 : 1240}
          renderRow={(order, index) => {
            const orderId = Number(order.orderId);
            const canNavigate = Number.isFinite(orderId) && orderId > 0;

            const displayIndex = (page - 1) * pageSize + index + 1;
            const statusMeta = getOrderStatusMeta(order.orderStatus);

            return (
              <>
                {/* Index (clickable) */}
                <TableCell
                  align="right"
                  sx={{
                    ...cellSx.number,
                    cursor: canNavigate ? 'pointer' : 'default',
                    textDecoration: canNavigate ? 'underline' : 'none',
                    textUnderlineOffset: canNavigate ? '3px' : undefined,
                    '&:hover': canNavigate ? { opacity: 0.85 } : undefined,
                  }}
                  onClick={() => {
                    if (!canNavigate) return;
                    goDetail(orderId);
                  }}
                >
                  {displayIndex}
                </TableCell>

                {/* Order Code (clickable) */}
                <TableCell
                  align="left"
                  sx={[
                    cellSx.mono,
                    {
                      cursor: canNavigate ? 'pointer' : 'default',
                      textDecoration: canNavigate ? 'underline' : 'none',
                      textUnderlineOffset: canNavigate ? '3px' : undefined,
                      color: canNavigate ? 'primary.main' : 'text.primary',
                      fontWeight: 600,
                      '&:hover': canNavigate ? { color: 'primary.dark' } : undefined,
                    },
                  ]}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!canNavigate) return;
                    goDetail(orderId);
                  }}
                >
                  {order.orderCode}
                </TableCell>

                <TableCell align="left" sx={cellSx.mono}>
                  {order.product.productCode}
                </TableCell>

                <TableCell align="left" sx={cellSx.nowrap}>
                  {order.product.productName}
                </TableCell>

                {!isPartner && (
                  <TableCell align="center" sx={cellSx.nowrap}>
                    <Chip label={order.stock.stockCode} color="error" size="small" variant="outlined" />
                  </TableCell>
                )}

                <TableCell align="right" sx={cellSx.number}>
                  {order.quantity}
                </TableCell>

                <TableCell align="right" sx={cellSx.money}>
                  {toUsdDisplay(order.paymentAmount)}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <Chip label={order.partner.name} color="primary" size="small" variant="outlined" />
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  {formatKSTDateOnly(order.reservedAt)}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  {formatKSTDateTime(order.createdAt)}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <Chip
                    label={statusMeta.label}
                    color={statusMeta.chip.color}
                    variant={statusMeta.chip.variant}
                    size="small"
                  />
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      <Box mt={2}>
        <StandardPagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount ?? 0}
          onChangePage={handleChangePage}
          size="medium"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default OrdersTable;
